import { PREVIEW_FRAME_ROOT, PREVIEW_MODE } from '$lib/global/constants';

import debug from 'debug';

import { prefersReducedMotion } from './accessibility';

const debugLog = debug('app:scroll-to-component');

interface ScrollConfig {
    dataAttribute: string;
    scrollBehavior: ScrollBehavior;
    waitTimeout: number;
    postNavigationDelay: number;
}

const DEFAULT_CONFIG: ScrollConfig = {
    dataAttribute: 'data-scroll-id',
    scrollBehavior: 'smooth',
    waitTimeout: 5000,
    postNavigationDelay: 150,
};

/**
 * Creates a scroll navigation module with configured behavior
 * @param config - Optional configuration to override defaults
 * @returns Scroll navigation functions
 */
const createScrollNavigation = (config: Partial<ScrollConfig> = {}) => {
    const settings = { ...DEFAULT_CONFIG, ...config };

    const componentExists = (componentId: string): boolean => {
        const element = document.querySelector(`[${settings.dataAttribute}="${componentId}"]`);
        return !!element;
    };

    const getHeaderHeight = (): number => {
        const header = document.querySelector('[aria-label="Site header"][data-is-fixed="true"]');

        if (header) {
            return header.getBoundingClientRect().height;
        }
        return 0;
    };

    const scrollToElement = (componentId: string): void => {
        const element = document.querySelector(`[${settings.dataAttribute}="${componentId}"]`);

        if (!element) {
            return;
        }

        // Respect user's motion preferences
        const behavior = prefersReducedMotion() ? 'auto' : settings.scrollBehavior;

        const y = element.getBoundingClientRect().top + window.scrollY - getHeaderHeight();

        if (PREVIEW_MODE) {
            const previewFrameRoot = document.getElementById(PREVIEW_FRAME_ROOT);

            if (previewFrameRoot) {
                previewFrameRoot.scrollTo({
                    top: y,
                    behavior,
                });
            }
        } else {
            window.scrollTo({ top: y, behavior });
        }

        if (element instanceof HTMLElement) {
            element.focus({ preventScroll: true });

            // Element not focusable, set tabindex to 0 and focus it
            if (document.activeElement !== element) {
                element.setAttribute('tabindex', '0');
                element.focus({ preventScroll: true });
            }
        }
    };

    const waitForComponent = (componentId: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (componentExists(componentId)) {
                resolve();
                return;
            }

            const observer = new MutationObserver(() => {
                if (componentExists(componentId)) {
                    observer.disconnect();
                    clearTimeout(timeout);
                    resolve();
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            const timeout = setTimeout(() => {
                observer.disconnect();
                reject(new Error(`Timeout waiting for component "${componentId}"`));
            }, settings.waitTimeout);
        });
    };

    const scrollOnSamePage = (componentId: string): void => {
        if (!componentId) {
            return;
        }

        scrollToElement(componentId);
    };

    const scrollAfterNavigation = async (componentId: string): Promise<void> => {
        if (!componentId) {
            return;
        }

        try {
            await waitForComponent(componentId);

            // Small delay to freeze the page and then scroll to the component
            setTimeout(() => {
                scrollToElement(componentId);
            }, settings.postNavigationDelay);
        } catch (error) {
            debugLog('Error waiting for component:', error);
        }
    };

    return {
        scrollOnSamePage,
        scrollAfterNavigation,
    };
};

export const scrollNavigation = createScrollNavigation();

export { createScrollNavigation };
